import { Link } from 'react-router-dom';
import {
  PenTool, Sparkles, FileText, Target, Award, TrendingUp,
  ChevronRight, Check, Star, Type, User, Minus, BookOpen, Hash, Italic,
  Upload, ShieldCheck
} from 'lucide-react';

export default function Home() {
  const features = [
    {
      icon: FileText,
      title: 'Free Worksheet Tools',
      description: 'Generate unlimited practice sheets for cursive, print, name tracing, and more.',
      color: 'bg-bastion-blue-500'
    },
    {
      icon: Sparkles,
      title: 'AI-Powered Analysis',
      description: 'Get detailed feedback on your handwriting with our advanced AI analysis.',
      color: 'bg-bastion-orange-500'
    },
    {
      icon: Target,
      title: 'Pen-Specific Practice',
      description: 'Worksheets optimized for fountain pens and ballpoint pens.',
      color: 'bg-emerald-600'
    },
    {
      icon: TrendingUp,
      title: 'Track Your Progress',
      description: 'See your improvement over time with detailed progress charts.',
      color: 'bg-violet-600'
    }
  ];

  const tools = [
    { name: 'Cursive Handwriting', slug: 'cursive', icon: Italic, bg: 'bg-purple-100', text: 'text-purple-600' },
    { name: 'Print Handwriting', slug: 'print', icon: Type, bg: 'bg-bastion-blue-50', text: 'text-bastion-blue-500' },
    { name: 'Name Tracing', slug: 'name-tracing', icon: User, bg: 'bg-green-100', text: 'text-green-600' },
    { name: 'Tracing Lines', slug: 'tracing-lines', icon: Minus, bg: 'bg-bastion-orange-50', text: 'text-bastion-orange-500' },
    { name: 'Alphabet Tracing', slug: 'alphabet-tracing', icon: BookOpen, bg: 'bg-pink-100', text: 'text-pink-600' },
    { name: 'Number Tracing', slug: 'number-tracing', icon: Hash, bg: 'bg-teal-100', text: 'text-teal-600' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-bastion-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-bastion-blue-50 text-bastion-blue-500 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Free Worksheets + AI-Powered Improvement
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-bastion-black mb-6">
              Transform Your{' '}
              <span className="text-bastion-blue-500">Handwriting</span>{' '}
              Today
            </h1>

            <p className="text-xl text-bastion-gray-800 mb-8">
              Create custom practice worksheets and get AI-powered analysis to improve your
              penmanship. Perfect for students, adults, and pen enthusiasts.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/analysis" className="btn-primary text-lg py-4 px-8">
                <Sparkles className="w-5 h-5" />
                Try AI Analysis
                <span className="text-sm bg-white/20 px-2 py-0.5 rounded">Free first try</span>
              </Link>
              <Link to="/tools" className="btn-secondary text-lg py-4 px-8">
                <FileText className="w-5 h-5" />
                Create Worksheets
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 mt-8 text-base text-bastion-gray-800 font-semibold">
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                Professional Worksheets
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                AI-Powered Analysis
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                Easy to Get Started
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Everything You Need to Improve</h2>
            <p className="section-subtitle">
              Comprehensive tools for handwriting improvement at any skill level
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card-hover">
                <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-bastion-black mb-2">
                  {feature.title}
                </h3>
                <p className="text-bastion-gray-800 text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Worksheet Tools Section */}
      <section className="py-20 bg-bastion-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Free Handwriting Worksheets</h2>
            <p className="section-subtitle">
              Create unlimited printable PDFs with instant download
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => {
              const ToolIcon = tool.icon;
              return (
                <Link
                  key={index}
                  to={`/tools/${tool.slug}`}
                  className="card-hover flex items-center justify-between group"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${tool.bg} rounded-lg flex items-center justify-center`}>
                      <ToolIcon className={`w-6 h-6 ${tool.text}`} />
                    </div>
                    <span className="font-medium text-bastion-black">{tool.name}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-bastion-gray-400 group-hover:text-bastion-black transition-colors" />
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link to="/tools" className="btn-outline">
              View All Tools
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* AI Analysis Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <div>
              <div className="inline-flex items-center gap-2 bg-bastion-orange-50 text-bastion-orange-500 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Evidence-Based Analysis
              </div>

              <h2 className="section-title">AI Handwriting Analysis</h2>
              <p className="section-subtitle">
                Upload a photo of your handwriting and get a detailed report scoring 5 key dimensions — with specific tips to improve your weakest areas.
              </p>

              <div className="mt-8 space-y-5">
                {[
                  { icon: Award, label: 'Overall Score with Peer Benchmark', desc: 'See where you stand compared to writers at your level.' },
                  { icon: Target, label: '5-Dimension Scoring', desc: 'Individual scores for Legibility, Consistency, Fluency, Structure, and Pen Control.' },
                  { icon: TrendingUp, label: 'Actionable Improvement Tips', desc: 'Targeted exercises and techniques for your specific weak spots.' },
                  { icon: FileText, label: 'Personalized Practice Worksheets', desc: 'One-click generation of worksheets that target your results.' },
                  { icon: PenTool, label: 'Any Pen, Any Paper', desc: 'Fountain pen, ballpoint, or pencil on lined or unlined paper.' },
                  { icon: ShieldCheck, label: 'Privacy-First', desc: 'No signup required for your first analysis. Delete results anytime.' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-bastion-orange-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-5 h-5 text-bastion-orange-500" />
                    </div>
                    <div>
                      <span className="font-semibold text-bastion-black">{item.label}</span>
                      <span className="text-bastion-gray-800"> — {item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/analysis" className="btn-primary">
                  <Sparkles className="w-5 h-5" />
                  Try AI Analysis Free
                  <span className="text-sm bg-white/20 px-2 py-0.5 rounded">Free first try</span>
                </Link>
                <a href="#sample-report" className="btn-secondary">
                  See Sample Report
                </a>
              </div>
            </div>

            {/* Right Column — Upgraded Sample Report */}
            <div id="sample-report" className="bg-bastion-gray-50 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-bastion-black">Sample Report</h3>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>

                {/* Score Ring */}
                <div className="flex flex-col items-center justify-center mb-6">
                  <div className="relative">
                    <svg className="w-36 h-36" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="52" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                      <circle
                        cx="60" cy="60" r="52"
                        fill="none" stroke="#22c55e" strokeWidth="10"
                        strokeDasharray={`${(78 / 100) * 2 * Math.PI * 52} ${2 * Math.PI * 52}`}
                        strokeLinecap="round"
                        transform="rotate(-90 60 60)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-bold text-bastion-black">78</span>
                      <span className="text-sm font-medium text-green-600">Good</span>
                    </div>
                  </div>
                  <p className="text-sm text-bastion-gray-800 mt-2">Above average for adult writers</p>
                </div>

                {/* 5 Dimension Bars */}
                <div className="space-y-3">
                  {[
                    { label: 'Legibility', score: 82 },
                    { label: 'Consistency', score: 75 },
                    { label: 'Fluency', score: 80 },
                    { label: 'Structure', score: 72 },
                    { label: 'Pen Control', score: 78 }
                  ].map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-bastion-gray-800">{item.label}</span>
                        <span className="font-medium">{item.score}</span>
                      </div>
                      <div className="h-2 bg-bastion-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-bastion-blue-500 rounded-full"
                          style={{ width: `${item.score}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Top Recommendation */}
                <div className="mt-5 bg-bastion-gray-50 rounded-lg p-4 border border-bastion-gray-200">
                  <p className="text-sm font-semibold text-bastion-black mb-1">Top Recommendation</p>
                  <p className="text-sm text-bastion-gray-800">
                    Focus on letter spacing consistency — try the even spacing worksheet exercise.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-bastion-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              From photo to personalized improvement plan in under 60 seconds
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Upload, step: '1', title: 'Snap a Photo', desc: 'Take a clear photo of your handwriting on any paper.' },
              { icon: Sparkles, step: '2', title: 'AI Analyzes Your Writing', desc: 'Scored across 5 dimensions with detailed feedback in seconds.' },
              { icon: TrendingUp, step: '3', title: 'Practice & Improve', desc: 'Follow personalized tips and generate targeted worksheets.' }
            ].map((item, index) => (
              <div key={index} className="text-center bg-white rounded-xl p-8 shadow-sm">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-bastion-blue-500 rounded-full mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-sm font-medium text-bastion-gray-800 mb-2">Step {item.step}</div>
                <h3 className="text-xl font-bold text-bastion-black mb-2">{item.title}</h3>
                <p className="text-bastion-gray-800 text-base leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-4">
            {[
              'Evidence-based, not graphology',
              'Used by teachers & therapists',
              'All ages & skill levels',
              'Your data stays private'
            ].map((text, index) => (
              <span key={index} className="flex items-center gap-2 text-bastion-gray-800 font-medium">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                {text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-bastion-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Improve Your Handwriting?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start with free worksheets or try our AI analysis to see where you can improve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-white text-bastion-black hover:bg-bastion-gray-100 font-semibold py-4 px-8 rounded-lg transition-colors inline-flex items-center justify-center gap-2">
              Get Started Free
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="bg-transparent text-white border-2 border-white hover:bg-white/10 font-semibold py-4 px-8 rounded-lg transition-colors">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
